import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Button, Input, Select, Space, Tag, Popconfirm, Modal, Form, message } from 'antd';
import { PlusOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import cdnToolsService from '../../services/cdnToolsService';

const DURATIONS = [
  { value: 3600, label: '1 小时' },
  { value: 86400, label: '24 小时' },
  { value: 604800, label: '7 天' },
  { value: 0, label: '永久' },
];

const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const isIpOrCidr = (v) => {
  const [ip, mask, extra] = String(v || '').trim().split('/');
  if (extra !== undefined || !ip) return false;
  const v4 = IPV4.test(ip);
  const v6 = !v4 && ip.includes(':') && /^[0-9a-fA-F:.]+$/.test(ip);
  if (!v4 && !v6) return false;
  if (mask === undefined) return true;
  if (!/^\d+$/.test(mask)) return false;
  return Number(mask) <= (v4 ? 32 : 128);
};

const fmt = (t) => (t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '-');

export default function Banlist() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    const res = await cdnToolsService.getBans({ page, per_page: pageSize, keyword: keyword || undefined });
    if (res.success) {
      setRows(res.data?.items || []);
      setTotal(res.data?.pagination?.total || 0);
      setError(null);
    } else {
      setError(res.error || '加载失败');
      message.error(res.error || '加载封禁列表失败');
    }
    setLoading(false);
  }, [page, pageSize, keyword]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    const res = await cdnToolsService.addBan({
      ip: values.ip.trim(),
      ttl_secs: values.ttl_secs,
      reason: (values.reason || '').trim(),
    });
    setSaving(false);
    if (res.success) {
      message.success(`已封禁 ${values.ip.trim()}，正在下发到全部节点`);
      setOpen(false);
      form.resetFields();
      if (page !== 1) setPage(1); else load();
    } else {
      message.error(res.error || '封禁失败');
    }
  };

  const unban = async (row) => {
    setRemovingId(row.id);
    const res = await cdnToolsService.removeBan(row.id);
    setRemovingId(null);
    if (res.success) {
      message.success(`已解封 ${row.device_value}`);
      load();
    } else {
      message.error(res.error || '解封失败');
    }
  };

  const columns = [
    {
      title: 'IP / CIDR',
      dataIndex: 'device_value',
      render: (v) => <code>{v}</code>,
    },
    {
      title: '原因',
      dataIndex: 'ban_reason',
      ellipsis: true,
      render: (v) => v || '-',
    },
    {
      title: '到期',
      dataIndex: 'ban_time',
      width: 190,
      render: (v) => {
        if (!v) return <Tag color="red">永久</Tag>;
        const expired = dayjs(v).isBefore(dayjs());
        return expired ? <Tag>已过期 {fmt(v)}</Tag> : fmt(v);
      },
    },
    {
      title: '来源',
      dataIndex: 'source',
      width: 100,
      render: (v) => v || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: fmt,
    },
    {
      title: '操作',
      key: 'op',
      width: 90,
      render: (_, row) => (
        <Popconfirm
          title={`解封 ${row.device_value}？`}
          onConfirm={() => unban(row)}
          okText="解封"
          cancelText="取消"
        >
          <Button type="link" danger size="small" loading={removingId === row.id}>解封</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title={<Space><StopOutlined />封禁 IP</Space>}
      extra={
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="搜索 IP"
            style={{ width: 200 }}
            onSearch={(v) => { setPage(1); setKeyword(v.trim()); }}
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>添加封禁</Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 760 }}
        locale={{ emptyText: error ? `加载失败：${error}` : '暂无封禁' }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, s) => { setPage(p); setPageSize(s); },
        }}
      />

      <Modal
        title="添加封禁"
        open={open}
        onOk={submit}
        onCancel={() => setOpen(false)}
        okText="封禁"
        okButtonProps={{ danger: true }}
        cancelText="取消"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ ttl_secs: 86400 }} preserve={false}>
          <Form.Item
            name="ip"
            label="IP 或 CIDR"
            rules={[
              { required: true, message: '请输入 IP 或 CIDR' },
              { validator: (_, v) => (!v || isIpOrCidr(v) ? Promise.resolve() : Promise.reject(new Error('格式不正确'))) },
            ]}
          >
            <Input placeholder="1.2.3.4 或 1.2.3.0/24" autoFocus />
          </Form.Item>
          <Form.Item name="ttl_secs" label="时长">
            <Select options={DURATIONS} />
          </Form.Item>
          <Form.Item name="reason" label="原因">
            <Input maxLength={128} placeholder="可选" />
          </Form.Item>
        </Form>
        <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>全部边缘节点生效，同一 IP 重复提交会覆盖时长与原因。</div>
      </Modal>
    </Card>
  );
}
